/**
 * Splash audio. Wall bounce: /splash/boing.mp3
 *
 * Unlock in a user gesture (login Continue). Timeline arm schedules Web Audio boings
 * at +1.0, +1.75, +2.55s from arm time; rAF falls back to HTML pool when schedule fails.
 */
import { readSplashSoundEnabled } from './splashConstants';

const BOING_URL = '/splash/boing.mp3';
const BOING_VOLUME = 0.9;
const PRIME_VOLUME = 0.001;
const PRIME_PAUSE_MS = 12;
const POOL_SIZE = 4;

/** @type {HTMLAudioElement[]} */
let htmlPool = [];
let poolIndex = 0;
let splashAudioUnlocked = false;
let lastUnlockMs = 0;
let webAudioScheduledOnArm = false;
/** performance.now() when timeline audio was last armed */
let lastArmPerformanceMs = 0;

let audioCtx = null;
/** @type {AudioBuffer | null} */
let boingBuffer = null;
/** @type {Promise<AudioBuffer | null> | null} */
let boingLoadPromise = null;
/** Impact indices scheduled via Web Audio for the current arm. */
let webAudioScheduledImpacts = new Set();

/** Timeline of what the splash audio did on this device — posted to the backend so TV-only problems can be diagnosed. */
const splashAudioLog = [];
function note(ev, data) {
  try {
    if (splashAudioLog.length < 80) splashAudioLog.push({ t: Math.round(performance.now()), ev, ...(data || {}) });
  } catch (_) {}
}

export function flushSplashAudioLog() {
  if (typeof window === 'undefined' || !splashAudioLog.length) return;
  try {
    const body = JSON.stringify({ ua: window.navigator.userAgent, events: splashAudioLog.splice(0) });
    fetch('/api/debug/splash-audio', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body,
      keepalive: true,
    }).catch(() => {});
  } catch (_) {}
}

/** ctx.currentTime / performance.now() at the last arm — to tell a live audio clock from a stalled one. */
let armCtxTime = 0;
let armPerfMs = 0;

/**
 * Is Web Audio really producing sound? A context can say "running" while its clock stands still (seen on TV
 * WebViews); anything scheduled on it then never plays. The splash falls back to plain <audio> in that case.
 */
export function isSplashWebAudioHealthy() {
  if (!audioCtx || audioCtx.state !== 'running') return false;
  const wallS = (performance.now() - armPerfMs) / 1000;
  if (wallS < 0.5) return true;
  return audioCtx.currentTime - armCtxTime > wallS * 0.5;
}

function getAudioContext() {
  if (typeof window === 'undefined') return null;
  const Ctx = window.AudioContext || window.webkitAudioContext;
  if (!Ctx) return null;
  if (!audioCtx) audioCtx = new Ctx();
  return audioCtx;
}

function withAudioContext(run) {
  const ctx = getAudioContext();
  if (!ctx) return;
  const exec = () => {
    try {
      run(ctx);
    } catch (_) {}
  };
  if (ctx.state === 'suspended') {
    void ctx.resume().then(exec).catch(() => {});
  } else {
    exec();
  }
}

function ensureHtmlPool() {
  if (typeof window === 'undefined' || htmlPool.length > 0) return htmlPool;
  for (let i = 0; i < POOL_SIZE; i += 1) {
    const el = new Audio(BOING_URL);
    el.preload = 'auto';
    el.volume = BOING_VOLUME;
    htmlPool.push(el);
  }
  return htmlPool;
}

function loadBoingBuffer(ctx) {
  if (boingBuffer) return Promise.resolve(boingBuffer);
  if (!boingLoadPromise) {
    boingLoadPromise = fetch(BOING_URL)
      .then((res) => {
        if (!res.ok) throw new Error(`boing fetch ${res.status}`);
        return res.arrayBuffer();
      })
      .then(
        (data) =>
          new Promise((resolve, reject) => {
            ctx.decodeAudioData(data, resolve, reject);
          }),
      )
      .then((buf) => {
        boingBuffer = buf;
        return buf;
      })
      .catch(() => null);
  }
  return boingLoadPromise;
}

if (typeof window !== 'undefined' && readSplashSoundEnabled()) {
  ensureHtmlPool();
  const ctx = getAudioContext();
  if (ctx) void loadBoingBuffer(ctx);
}

export function isSplashAudioUnlocked() {
  return splashAudioUnlocked;
}

export function isSplashAudioUnlockedRecently(withinMs) {
  if (!splashAudioUnlocked) return false;
  return performance.now() - lastUnlockMs < withinMs;
}

export function resetSplashBoingScheduleState() {
  clearBoingTimers();
  webAudioScheduledImpacts = new Set();
  webAudioScheduledOnArm = false;
}

export function wasSplashBoingWebAudioScheduledOnArm() {
  return webAudioScheduledOnArm;
}

export function isSplashBoingWebAudioScheduled(impactIndex) {
  return webAudioScheduledImpacts.has(impactIndex);
}

/** The TV app's WebView (the `__zflixAndroidWebView` flag is injected after page load, so also sniff the UA). */
function isAndroidTvWebView() {
  if (typeof window === 'undefined') return false;
  if (window.__zflixAndroidWebView === true) return true;
  const ua = String(window.navigator?.userAgent || '');
  /** The TV app sends its own browser name ("... ZFLIX-TV/1.0"), with no "; wv)" or AFT model in it. Without this
   *  the answer depended on whether the app's flag had been injected yet, so the sound lead was sometimes 0. */
  if (/ZFLIX-TV\//.test(ua)) return true;
  return /Android/.test(ua) && (/; wv\)/.test(ua) || /\bAFT[A-Z0-9]+\b/.test(ua));
}

/**
 * How far ahead of the visual impact a boing must start so it is *heard* on the impact:
 * the device's audio output latency. Android WebViews often report none, so assume a typical pipeline delay there.
 */
function boingLeadSeconds(ctx) {
  /** Desktop browsers were already in step with no lead — only compensate on the TV app. */
  if (!isAndroidTvWebView()) return 0;
  /** A Fire TV Stick reported outputLatency 0.488s + baseLatency 0.144s: sound leaves the TV ~0.6s after it is started. */
  const reported = (Number(ctx.outputLatency) || 0) + (Number(ctx.baseLatency) || 0);
  const lead = reported > 0.005 ? reported : 0.08;
  return Math.min(0.9, Math.max(0, lead));
}

/**
 * TV WebViews update `ctx.currentTime` in coarse jumps (it was seen standing still for over half a second), so
 * "now + delay" lands anywhere inside a jump. Track the highest (currentTime − wall clock) seen instead: the
 * clock only ever lags the truth, so the maximum is the real offset between the two clocks.
 */
let clockOffsetMax = -Infinity;
let clockSampler = null;

function sampleAudioClock() {
  if (!audioCtx || audioCtx.state !== 'running') return;
  const off = audioCtx.currentTime - performance.now() / 1000;
  if (off > clockOffsetMax) clockOffsetMax = off;
}

let clockSamplerTicks = 0;

function startClockSampler() {
  if (typeof window === 'undefined') return;
  /** Each call buys another ~12s of sampling (login tap → intro can be several seconds apart). */
  clockSamplerTicks = 0;
  if (clockSampler) return;
  if (audioCtx && !audioCtx.onstatechange) {
    /** A suspend/resume shifts the two clocks against each other — old samples no longer apply. */
    audioCtx.onstatechange = () => {
      clockOffsetMax = -Infinity;
    };
  }
  clockSampler = window.setInterval(() => {
    sampleAudioClock();
    clockSamplerTicks += 1;
    if (clockSamplerTicks > 600) {
      window.clearInterval(clockSampler);
      clockSampler = null;
    }
  }, 20);
}

/**
 * Context time at which a sound must START to be HEARD at wall-clock `targetPerfMs`.
 * Desktop: plain currentTime (smooth there, and already in step). TV: the output timestamp when the WebView
 * provides a sane one (it maps "what is leaving the speaker" to wall time directly), else sampled offset − latency.
 */
function contextTimeToBeHeardAt(ctx, targetPerfMs) {
  if (!isAndroidTvWebView()) {
    return { at: ctx.currentTime + Math.max(0, (targetPerfMs - performance.now()) / 1000), mode: 'now' };
  }
  sampleAudioClock();
  const lead = boingLeadSeconds(ctx);
  const haveSamples = Number.isFinite(clockOffsetMax);
  let offA = null;
  try {
    const ts = typeof ctx.getOutputTimestamp === 'function' ? ctx.getOutputTimestamp() : null;
    if (ts && ts.performanceTime > 0 && ts.contextTime > 0) offA = ts.contextTime - ts.performanceTime / 1000;
  } catch (_) {}
  const offB = haveSamples ? clockOffsetMax - lead : null;
  /** Trust the output timestamp only when it sits where an output clock should: behind the context clock, by under ~1.5s. */
  const aSane = offA != null && haveSamples && offA <= clockOffsetMax + 0.05 && offA >= clockOffsetMax - 1.5;
  const off = aSane ? offA : offB != null ? offB : ctx.currentTime - performance.now() / 1000 - lead;
  const at = Math.max(ctx.currentTime, off + targetPerfMs / 1000);
  return {
    at,
    mode: aSane ? 'outputTs' : 'sampled',
    offA: offA == null ? null : +offA.toFixed(3),
    offB: offB == null ? null : +offB.toFixed(3),
    lead: +lead.toFixed(3),
  };
}

/**
 * A just-resumed context reports "running" before its clock actually advances (the audio device is still
 * spinning up). Anything scheduled in that window plays late, so nudge the device and wait for the clock to move.
 */
function waitForAudioClock(ctx, maxMs) {
  try {
    const buf = ctx.createBuffer(1, 1, ctx.sampleRate);
    const src = ctx.createBufferSource();
    src.buffer = buf;
    const gain = ctx.createGain();
    gain.gain.value = 0;
    src.connect(gain);
    gain.connect(ctx.destination);
    src.start(0);
  } catch (_) {}
  return new Promise((resolve) => {
    const startedAt = performance.now();
    const c0 = ctx.currentTime;
    const poll = () => {
      if (ctx.currentTime - c0 > 0.03) return resolve(true);
      if (performance.now() - startedAt > maxMs) return resolve(false);
      window.setTimeout(poll, 20);
      return undefined;
    };
    poll();
  });
}

/**
 * Get audio ready *before* the splash animation starts: context running and the boing decoded.
 * On the TV app there is no login tap, so without this the first boing waits on fetch/decode and plays late.
 * Resolves false quickly when the browser will not start audio without a gesture.
 * @returns {Promise<boolean>}
 */
export function prepareSplashAudio() {
  if (typeof window === 'undefined' || !readSplashSoundEnabled()) return Promise.resolve(false);
  const ctx = getAudioContext();
  if (!ctx) return Promise.resolve(false);
  const after = (ms) => new Promise((resolve) => window.setTimeout(() => resolve(false), ms));
  const resumeWaitMs = isAndroidTvWebView() ? 900 : 150;
  const bufferWaitMs = isAndroidTvWebView() ? 2000 : 800;
  let resumed;
  try {
    resumed =
      ctx.state === 'running'
        ? Promise.resolve(true)
        : Promise.race([ctx.resume().then(() => ctx.state === 'running').catch(() => false), after(resumeWaitMs)]);
  } catch (_) {
    return Promise.resolve(false);
  }
  startClockSampler();
  note('prepare', { state: ctx.state, tv: isAndroidTvWebView(), hasBuffer: !!boingBuffer });
  return resumed.then((running) => {
    note('resumed', { running, state: ctx.state });
    if (!running) return false;
    return Promise.all([
      Promise.race([loadBoingBuffer(ctx).then((buf) => !!buf), after(bufferWaitMs)]),
      waitForAudioClock(ctx, isAndroidTvWebView() ? 1500 : 400),
    ]).then(([haveBuffer, clockMoving]) => {
      note('ready', { haveBuffer: !!haveBuffer, clockMoving: !!clockMoving, ctxTime: +ctx.currentTime.toFixed(3) });
      return !!haveBuffer && !!clockMoving;
    });
  });
}

/** Pending per-impact timers (cleared on reset so a re-arm never doubles a boing). */
let boingTimers = [];

function clearBoingTimers() {
  boingTimers.forEach((id) => window.clearTimeout(id));
  boingTimers = [];
}

/**
 * How long before an impact its boing is handed to the audio clock. On the TV it must cover the ~0.6s output
 * latency plus timers that run a quarter-second late while the app loads.
 */
function boingLookaheadMs() {
  return isAndroidTvWebView() ? 1300 : 350;
}

/**
 * Each boing is put on the audio clock shortly before its impact, measured against the same clock the animation
 * uses (performance.now). Scheduling all three seconds ahead trusted the audio clock to have run steadily since
 * arm time, which it does not on a cold device.
 */
function scheduleBoingsFromArm(ctx, impactTimesS, splashElapsedS) {
  if (!boingBuffer) return false;
  const timelineZeroMs = performance.now() - splashElapsedS * 1000;
  armCtxTime = ctx.currentTime;
  armPerfMs = performance.now();
  startClockSampler();
  note('schedule', { elapsedS: +splashElapsedS.toFixed(3), state: ctx.state, ctxTime: +ctx.currentTime.toFixed(3), lead: boingLeadSeconds(ctx), outLat: ctx.outputLatency, baseLat: ctx.baseLatency });
  const fire = (impactS) => {
    try {
      const targetPerfMs = timelineZeroMs + impactS * 1000;
      const remainingS = (targetPerfMs - performance.now()) / 1000;
      const map = contextTimeToBeHeardAt(ctx, targetPerfMs);
      const at = map.at;
      note('fire', { impactS, state: ctx.state, ctxTime: +ctx.currentTime.toFixed(3), remainingS: +remainingS.toFixed(3), startsInS: +(at - ctx.currentTime).toFixed(3), mode: map.mode, offA: map.offA, offB: map.offB, lead: map.lead, healthy: isSplashWebAudioHealthy() });
      const src = ctx.createBufferSource();
      src.buffer = boingBuffer;
      const gain = ctx.createGain();
      gain.gain.setValueAtTime(BOING_VOLUME, at);
      src.connect(gain);
      gain.connect(ctx.destination);
      src.start(at);
    } catch (_) {}
  };
  for (let i = 0; i < impactTimesS.length; i += 1) {
    if (webAudioScheduledImpacts.has(i)) continue;
    const waitMs = timelineZeroMs + impactTimesS[i] * 1000 - performance.now() - boingLookaheadMs();
    if (waitMs <= 0) fire(impactTimesS[i]);
    else boingTimers.push(window.setTimeout(() => fire(impactTimesS[i]), waitMs));
    webAudioScheduledImpacts.add(i);
  }
  const ok = webAudioScheduledImpacts.size === impactTimesS.length;
  webAudioScheduledOnArm = ok;
  return ok;
}

/**
 * Schedule boings at impact offsets from arm time (sync in gesture / mount stack).
 * @param {number[]} impactTimesS seconds from timeline arm
 * @param {number} [elapsedS=0] seconds already elapsed on the visual timeline
 * @returns {boolean} true if all impacts were scheduled with a decoded buffer
 */
export function armSplashTimelineAudio(impactTimesS, elapsedS = 0) {
  if (typeof window === 'undefined' || !readSplashSoundEnabled()) return false;
  webAudioScheduledOnArm = false;
  clearBoingTimers();
  webAudioScheduledImpacts = new Set();
  lastArmPerformanceMs = performance.now();

  const ctx = getAudioContext();
  if (!ctx) return false;
  note('arm', { elapsedS: +Number(elapsedS).toFixed(3), state: ctx.state, hasBuffer: !!boingBuffer, unlocked: splashAudioUnlocked });

  const runSchedule = (splashElapsedS) => {
    try {
      if (ctx.state === 'suspended') {
        /** Resuming takes real time on TV hardware — schedule against the timeline as it is *after* the resume. */
        void ctx
          .resume()
          .then(() =>
            scheduleBoingsFromArm(ctx, impactTimesS, elapsedS + (performance.now() - lastArmPerformanceMs) / 1000),
          )
          .catch(() => {});
        return false;
      }
      return scheduleBoingsFromArm(ctx, impactTimesS, splashElapsedS);
    } catch (_) {
      return false;
    }
  };

  if (boingBuffer) return runSchedule(elapsedS);

  void loadBoingBuffer(ctx).then((buf) => {
    if (!buf || !lastArmPerformanceMs) return;
    const splashElapsedS = elapsedS + (performance.now() - lastArmPerformanceMs) / 1000;
    runSchedule(splashElapsedS);
  });
  return false;
}

function resetPoolElement(el) {
  try {
    el.pause();
    el.currentTime = 0;
    el.volume = BOING_VOLUME;
  } catch (_) {}
}

function primeHtmlPoolSync(pool) {
  for (const el of pool) {
    try {
      el.volume = PRIME_VOLUME;
      el.currentTime = 0;
      const p = el.play();
      resetPoolElement(el);
      window.setTimeout(() => resetPoolElement(el), PRIME_PAUSE_MS);
      if (p && typeof p.then === 'function') {
        void p.then(() => resetPoolElement(el)).catch(() => {});
      } else if (p && typeof p.catch === 'function') {
        void p.catch(() => {});
      }
    } catch (_) {}
  }
}

function primeWebAudioSync() {
  const ctx = getAudioContext();
  if (!ctx) return;
  try {
    if (ctx.state === 'suspended') void ctx.resume();
  } catch (_) {}
  try {
    const buf = ctx.createBuffer(1, 1, ctx.sampleRate);
    const src = ctx.createBufferSource();
    src.buffer = buf;
    const gain = ctx.createGain();
    gain.gain.value = 0;
    src.connect(gain);
    gain.connect(ctx.destination);
    src.start(0);
    src.stop(ctx.currentTime + 0.001);
  } catch (_) {}
}

function playHtmlBoing() {
  if (!htmlPool.length) return Promise.resolve(false);
  const el = htmlPool[poolIndex % htmlPool.length];
  poolIndex += 1;
  try {
    el.currentTime = 0;
    el.volume = BOING_VOLUME;
    const p = el.play();
    if (p && typeof p.then === 'function') {
      return p.then(() => true).catch(() => false);
    }
    return Promise.resolve(true);
  } catch (_) {
    return Promise.resolve(false);
  }
}

function playBoingWebAudio() {
  const ctx = getAudioContext();
  if (!ctx) return;

  const play = () => {
    if (!boingBuffer) {
      loadBoingBuffer(ctx).then((buffer) => {
        if (!buffer) return;
        const src = ctx.createBufferSource();
        src.buffer = buffer;
        const gain = ctx.createGain();
        gain.gain.setValueAtTime(BOING_VOLUME, ctx.currentTime);
        src.connect(gain);
        gain.connect(ctx.destination);
        src.start(0);
      });
      return;
    }
    const src = ctx.createBufferSource();
    src.buffer = boingBuffer;
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(BOING_VOLUME, ctx.currentTime);
    src.connect(gain);
    gain.connect(ctx.destination);
    src.start(0);
  };

  if (ctx.state === 'suspended') {
    void ctx.resume().then(play).catch(() => {});
  } else {
    play();
  }
}

/**
 * Unlock splash audio inside a user gesture (login Continue).
 * Does not arm the timeline or schedule future boings.
 */
export function unlockSplashAudioFromGesture(_event) {
  if (typeof window === 'undefined' || !readSplashSoundEnabled()) return;
  const pool = ensureHtmlPool();
  primeHtmlPoolSync(pool);
  primeWebAudioSync();
  splashAudioUnlocked = true;
  lastUnlockMs = performance.now();
  startClockSampler();
  const ctx = getAudioContext();
  if (ctx) {
    try {
      void loadBoingBuffer(ctx);
    } catch (_) {}
  }
}

/** @deprecated Prefer unlockSplashAudioFromGesture inside a gesture handler. */
export function unlockSplashAudio() {
  unlockSplashAudioFromGesture(null);
}

export function preloadBoingSound() {
  if (typeof window === 'undefined' || !readSplashSoundEnabled()) return;
  ensureHtmlPool();
  withAudioContext((ctx) => {
    void loadBoingBuffer(ctx);
  });
}

/**
 * Wall-hit boing — HTML pool when unlocked; Web Audio fallback on reject.
 * `preferHtml` forces the plain <audio> path first (used when Web Audio is not actually producing sound).
 */
export function playBoing({ preferHtml = false } = {}) {
  if (typeof window === 'undefined' || !readSplashSoundEnabled()) return;
  note('playBoing', { preferHtml, unlocked: splashAudioUnlocked, state: audioCtx?.state });
  if (!splashAudioUnlocked && !preferHtml) {
    playBoingWebAudio();
    return;
  }
  ensureHtmlPool();
  void playHtmlBoing().then((ok) => {
    note('htmlBoing', { ok });
    if (!ok) playBoingWebAudio();
  });
}

/** Short original sting (Netflix-energy, not a copy). Respects mute. */
export function playSplashSting() {
  if (typeof window === 'undefined' || !readSplashSoundEnabled()) return;

  withAudioContext((ctx) => {
    const t0 = ctx.currentTime;
    const master = ctx.createGain();
    master.gain.setValueAtTime(0.0001, t0);
    master.gain.exponentialRampToValueAtTime(0.22, t0 + 0.02);
    master.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.55);
    master.connect(ctx.destination);

    const notes = [
      { f: 196, at: 0, dur: 0.12 },
      { f: 262, at: 0.07, dur: 0.1 },
      { f: 330, at: 0.14, dur: 0.14 },
      { f: 392, at: 0.22, dur: 0.2 },
    ];

    notes.forEach(({ f, at, dur }) => {
      const osc = ctx.createOscillator();
      const g = ctx.createGain();
      osc.type = 'triangle';
      osc.frequency.setValueAtTime(f, t0 + at);
      g.gain.setValueAtTime(0.0001, t0 + at);
      g.gain.exponentialRampToValueAtTime(0.35, t0 + at + 0.015);
      g.gain.exponentialRampToValueAtTime(0.0001, t0 + at + dur);
      osc.connect(g);
      g.connect(master);
      osc.start(t0 + at);
      osc.stop(t0 + at + dur + 0.05);
    });

    const noiseBuf = ctx.createBuffer(1, ctx.sampleRate * 0.08, ctx.sampleRate);
    const data = noiseBuf.getChannelData(0);
    for (let i = 0; i < data.length; i += 1) data[i] = (Math.random() * 2 - 1) * (1 - i / data.length);
    const noise = ctx.createBufferSource();
    noise.buffer = noiseBuf;
    const ng = ctx.createGain();
    ng.gain.setValueAtTime(0.0001, t0 + 0.18);
    ng.gain.exponentialRampToValueAtTime(0.08, t0 + 0.2);
    ng.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.38);
    noise.connect(ng);
    ng.connect(master);
    noise.start(t0 + 0.18);
    noise.stop(t0 + 0.4);
  });
}
